import { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaCommentDots, FaSearch, FaStar, FaSyncAlt, FaUsers, FaArrowRight } from "react-icons/fa";
import client from "../api/client";
import "./TeamFeedback.css";

const pastelColors = [
  { bg: "#ffedd5", text: "#c2410c", name: "peach" },      // peach/orange
  { bg: "#e9d5ff", text: "#7c3aed", name: "lavender" },   // lavender/purple
  { bg: "#d1fae5", text: "#059669", name: "mint" },        // mint green
  { bg: "#fef9c3", text: "#a16207", name: "yellow" },      // soft yellow
  { bg: "#fce7f3", text: "#db2777", name: "pink" },        // light pink
  { bg: "#dbeafe", text: "#1d4ed8", name: "blue" },        // pale blue
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const Stars = ({ rating }) => (
  <span className="team-feedback-stars" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => <FaStar key={star} className={star <= Number(rating) ? "filled" : ""} />)}
  </span>
);

export default function TeamFeedback() {
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const detailParams = useMemo(() => ({
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }), [search, from, to]);

  const loadSummaries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await client.get("/team-feedback/summaries", {
        params: search.trim() ? { search: search.trim() } : {},
      });
      setEmployees(Array.isArray(response.data?.employees) ? response.data.employees : []);
    } catch (requestError) {
      setEmployees([]);
      setError(requestError.response?.data?.message || "Unable to load employee feedback summaries.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  const loadEmployeeFeedback = useCallback(async () => {
    if (!selectedEmployee?.employee_id) return;
    setLoading(true);
    setError("");
    try {
      const response = await client.get(`/team-feedback/employee/${selectedEmployee.employee_id}`, { params: detailParams });
      setRecords(Array.isArray(response.data?.feedback) ? response.data.feedback : []);
    } catch (requestError) {
      setRecords([]);
      setError(requestError.response?.data?.message || "Unable to load employee feedback.");
    } finally {
      setLoading(false);
    }
  }, [detailParams, selectedEmployee?.employee_id]);

  useEffect(() => {
    if (selectedEmployee) loadEmployeeFeedback();
    else loadSummaries();
  }, [selectedEmployee, loadEmployeeFeedback, loadSummaries]);

  const selectEmployee = (employee) => {
    setSearch("");
    setFrom("");
    setTo("");
    setRecords([]);
    setSelectedEmployee(employee);
  };

  const returnToEmployees = () => {
    setSelectedEmployee(null);
    setSearch("");
    setFrom("");
    setTo("");
    setRecords([]);
  };

  const refresh = selectedEmployee ? loadEmployeeFeedback : loadSummaries;

  return (
    <main className="team-feedback-page">
      <header className="team-feedback-header">
        <div>
          <h1><FaCommentDots /> Team Feedback</h1>
          <p>{selectedEmployee ? "Feedback received by this employee." : "Choose an employee to review their received feedback."}</p>
        </div>
        <button type="button" onClick={refresh} disabled={loading} className="team-feedback-refresh">
          <FaSyncAlt /> Refresh
        </button>
      </header>

      {selectedEmployee ? (
        <>
          <section className="team-feedback-detail-heading">
            <button type="button" className="team-feedback-back" onClick={returnToEmployees}><FaArrowLeft /> Back to employees</button>
            <div className="team-feedback-avatar large">{selectedEmployee.employee_name?.trim().charAt(0)?.toUpperCase() || "?"}</div>
            <div><h2>{selectedEmployee.employee_name}</h2><p>{selectedEmployee.feedback_count ?? 0} feedback received</p></div>
          </section>
          <section className="team-feedback-filters" aria-label="Employee feedback filters">
            <label className="team-feedback-search"><FaSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search submitter name" /></label>
            <label>From<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label>To<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
          </section>
        </>
      ) : (
        <section className="team-feedback-filters" aria-label="Employee search">
          <label className="team-feedback-search"><FaSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employee name" /></label>
        </section>
      )}

      {error ? <div className="team-feedback-state error">{error}</div> : null}
      {loading ? <div className="team-feedback-state">Loading feedback…</div> : null}
      {!loading && !error && selectedEmployee && records.length === 0 ? <div className="team-feedback-state">No feedback received yet.</div> : null}
      {!loading && !error && !selectedEmployee && employees.length === 0 ? <div className="team-feedback-state">No employees match this search.</div> : null}

      {!loading && !error && !selectedEmployee && employees.length > 0 && (
        <section className="team-feedback-grid" aria-label="Employee feedback summaries">
          {employees.map((employee, index) => {
            const count = Number(employee.feedback_count) || 0;
            const average = Number(employee.average_rating);
            const colorIndex = index % pastelColors.length;
            const color = pastelColors[colorIndex];
            return (
              <button
                type="button"
                className="team-feedback-card"
                key={employee.employee_id}
                onClick={() => selectEmployee(employee)}
                style={{ "--pastel-bg": color.bg, "--pastel-text": color.text }}
              >
                <div className="team-feedback-card-header">
                  <div className="team-feedback-avatar">{employee.employee_name?.trim().charAt(0)?.toUpperCase() || "?"}</div>
                  <h3 className="team-feedback-card-name">{employee.employee_name}</h3>
                </div>
                <div className="team-feedback-card-body">
                  <div className="team-feedback-badge">
                    <FaCommentDots />
                    <span>{count} {count === 1 ? "feedback" : "feedback"}</span>
                  </div>
                  {count > 0 ? (
                    <div className="team-feedback-rating">
                      <Stars rating={average} />
                      <span>{average.toFixed(1)}</span>
                    </div>
                  ) : (
                    <div className="team-feedback-rating muted">No ratings yet</div>
                  )}
                </div>
                <div className="team-feedback-card-footer">
                  <span className="team-feedback-view-text">View feedback</span>
                  <div className="team-feedback-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              </button>
            );
          })}
        </section>
      )}

      {!loading && !error && selectedEmployee && records.length > 0 && (
        <section className="team-feedback-table-wrap">
          <table className="team-feedback-table">
            <thead><tr><th>Submitted by</th><th>Teammate</th><th>Rating</th><th>Reason / comment</th><th>Submitted at</th></tr></thead>
            <tbody>{records.map((record) => (
              <tr key={record.id}>
                <td>{record.submitted_by_name}</td><td>{record.teammate_name}</td>
                <td><Stars rating={record.rating} /></td>
                <td className="team-feedback-comment">
                  {record.reason ? <><strong>{record.reason}</strong>{record.comment ? <br /> : null}</> : null}
                  {record.comment || (!record.reason ? "—" : null)}
                </td>
                <td>{formatDateTime(record.created_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </main>
  );
}
